import Seo from "@/components/Seo";
import CTASection from "@/components/CTASection";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { AUDIENCES } from "@/data/content";

const APPROACH = [
  {
    title: "Risk-based testing",
    text: "We test what can hurt you most, first. Coverage follows business risk, not a checklist.",
  },
  {
    title: "Business-focused testing",
    text: "A defect matters because of what it does to users and revenue — that's how we frame it.",
  },
  {
    title: "Clear communication",
    text: "Every finding comes with steps, severity and evidence. You never guess what we meant.",
  },
  {
    title: "Early defect identification",
    text: "The cheapest defect is the one caught before release. We structure testing to find issues early.",
  },
  {
    title: "Continuous improvement",
    text: "Every cycle feeds the next — suites get sharper, coverage gets smarter, releases get safer.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Avirova Technologies — A Software QA & Testing Company"
        description="Avirova Technologies helps technology companies build and release software that users can trust. Risk-based, business-focused outsourced QA for startups, SaaS and product companies."
      />

      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-24" data-testid="about-hero">
        <div className="grid-lines absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
              {"// About Avirova Technologies"}
            </p>
          </MaskedLine>
          <h1 className="mt-6 max-w-4xl font-display text-5xl font-black leading-[0.98] tracking-tighter text-zinc-100 sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.2}>Quality is not a phase.</MaskedLine>
            <MaskedLine delay={0.32}>
              It's part of the <span className="text-volt">product.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.46}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              We believe testing isn't a gate at the end of development — it's a
              discipline woven into how good software gets built. Avirova Technologies exists
              to bring that discipline to teams that aren't ready for a large
              in-house QA department.
            </p>
          </MaskedLine>
        </div>
      </section>

      {/* MISSION */}
      <section className="border-t border-zinc-800/80" data-testid="mission-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
                  {"// Our mission"}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <p className="font-display text-3xl font-bold leading-snug tracking-tight text-zinc-100 sm:text-4xl lg:text-5xl">
                  To help technology companies build and release software that
                  users can <span className="text-volt">trust.</span>
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="border-t border-zinc-800/80" data-testid="approach-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHeader overline="Our approach" title="How we think about testing" />
          <div className="mt-16">
            {APPROACH.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div
                  data-testid={`approach-item-${i}`}
                  className="group grid items-baseline gap-4 border-t border-zinc-800 py-8 transition-colors duration-500 last:border-b hover:bg-white/[0.015] sm:grid-cols-12"
                >
                  <span className="font-mono text-sm text-volt sm:col-span-2">
                    {String(i + 1).padStart(2, "0")} //
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-zinc-100 sm:col-span-4">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-zinc-400 sm:col-span-6 sm:text-base">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="border-t border-zinc-800/80" data-testid="audiences-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <SectionHeader
            overline="Who we work with"
            title="Teams that build fast — and want to ship safe"
          />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {AUDIENCES.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={i * 0.07} className="h-full">
                  <div
                    data-testid={`audience-card-${i}`}
                    className="group h-full rounded-2xl border border-zinc-800 bg-ink-900/60 p-6 transition-colors duration-500 hover:border-volt/40"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-800 bg-ink-950 text-zinc-300 transition-colors duration-500 group-hover:text-volt">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-zinc-100">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-400">{a.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY WE EXIST */}
      <section className="border-t border-zinc-800/80" data-testid="why-we-exist-section">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeader overline="Why we exist" title="Great products ship without a QA department" />
            </div>
            <div className="space-y-6 lg:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-base leading-relaxed text-zinc-400 sm:text-lg">
                  Many growing companies have strong development teams but no
                  dedicated QA capacity. Developers test their own work under
                  time pressure, releases carry quiet risk, and users become the
                  real test environment.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="text-base leading-relaxed text-zinc-400 sm:text-lg">
                  Avirova Technologies provides flexible QA expertise without requiring you
                  to immediately build a large internal team. You get
                  experienced testers, structured processes and clear reporting
                  — scaled to what your product actually needs, when it needs it.
                </p>
              </Reveal>
              <Reveal delay={0.22}>
                <p className="border-l-2 border-volt pl-5 font-mono text-xs leading-relaxed tracking-wide text-zinc-500">
                  Note: team profiles, company history and verified client results
                  will be added here as they become available. [Placeholder]
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
